package co.maytes.standupbot.dashboard.features.standups

import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.mutableStateSetOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.Modal
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.AvatarTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Checkbox
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.roster.TextAction
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Composable
fun SlackUserPickerModal(
    directory: SlackDirectoryStore,
    alreadyOnTeam: Set<String>,
    onConfirm: (addSlackUserIds: List<String>, removeSlackUserIds: List<String>) -> Unit,
    onDismiss: () -> Unit,
) {
    var search by remember { mutableStateOf("") }
    val selection = remember { mutableStateSetOf<String>().apply { addAll(alreadyOnTeam) } }

    val filtered by remember(search, directory.users) {
        derivedStateOf {
            val q = search.lowercase().trim()
            if (q.isBlank()) directory.users
            else directory.users.filter { u ->
                u.displayName.lowercase().contains(q) ||
                    u.realName?.lowercase()?.contains(q) == true ||
                    u.slackUserId.lowercase().contains(q) ||
                    u.email?.lowercase()?.contains(q) == true
            }
        }
    }

    val adds by remember(selection, alreadyOnTeam) {
        derivedStateOf { (selection - alreadyOnTeam).toList() }
    }
    val removes by remember(selection, alreadyOnTeam) {
        derivedStateOf { (alreadyOnTeam - selection).toList() }
    }

    Modal(
        onDismiss = onDismiss,
        title = "Add standup members",
        widthPx = 560,
        footer = {
            SpanText(
                text = when {
                    adds.isEmpty() && removes.isEmpty() -> "No changes"
                    adds.isEmpty() -> "Remove ${removes.size}"
                    removes.isEmpty() -> "Add ${adds.size}"
                    else -> "Add ${adds.size} · Remove ${removes.size}"
                },
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary)
                    .styleModifier { property("flex", "1 1 auto") },
            )
            Button(
                label = "Cancel",
                variant = ButtonVariant.Ghost,
                onClick = onDismiss,
            )
            Button(
                label = "Save",
                variant = ButtonVariant.Primary,
                enabled = adds.isNotEmpty() || removes.isNotEmpty(),
                onClick = { onConfirm(adds, removes) },
            )
        },
    ) {
        SpanText(
            text = "Pick Slack users to include in this standup. Already-added users are pre-checked.",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary)
                .margin(bottom = Spacing.md),
        )

        TextInput(
            value = search,
            onValueChange = { search = it },
            modifier = Modifier.margin(bottom = Spacing.xs),
            placeholder = "Search by name, handle, or email…",
        )

        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.xs),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TextAction(label = if (search.isBlank()) "Select all" else "Select shown") {
                filtered.forEach { selection.add(it.slackUserId) }
            }
            TextAction(label = if (search.isBlank()) "None" else "Clear shown") {
                filtered.forEach { selection.remove(it.slackUserId) }
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("overflow-y", "auto")
                    property("flex", "1 1 auto")
                    property("min-height", "240px")
                    property("max-height", "480px")
                    property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                    property("border-radius", "${Radius.md}")
                }
                .padding(Spacing.xs),
        ) {
            when {
                directory.usersState is LoadState.Loading -> {
                    SpanText(
                        text = "Loading Slack workspace…",
                        modifier = Modifier.padding(Spacing.md).color(Palette.textTertiary),
                    )
                }
                filtered.isEmpty() -> {
                    SpanText(
                        text = if (search.isBlank()) "No Slack users available." else "No matches for \"$search\"",
                        modifier = Modifier.padding(Spacing.md).color(Palette.textTertiary),
                    )
                }
                else -> {
                    filtered.forEach { user ->
                        val isSelected = user.slackUserId in selection
                        UserRow(
                            user = user,
                            selected = isSelected,
                            alreadyOnTeam = user.slackUserId in alreadyOnTeam,
                            onToggle = {
                                if (isSelected) selection.remove(user.slackUserId)
                                else selection.add(user.slackUserId)
                            },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun UserRow(
    user: co.maytes.standupbot.shared.dto.slack.SlackDirectoryUserDto,
    selected: Boolean,
    alreadyOnTeam: Boolean,
    onToggle: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.sm)
            .cursor(Cursor.Pointer)
            .onClick { onToggle() }
            .styleModifier {
                property("user-select", "none")
                if (selected) property("background-color", Palette.accentSubtle.cssRgb())
            },
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Checkbox(
            checked = selected,
            onCheckedChange = { onToggle() },
            ariaLabel = user.displayName,
        )
        Avatar(
            name = user.displayName,
            src = user.avatarUrl,
            sizePx = 32,
            tone = AvatarTone.Accent,
        )
        Column(modifier = Modifier.styleModifier { property("flex", "1 1 auto") }) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
            ) {
                SpanText(
                    text = user.displayName,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .color(Palette.textPrimary)
                        .fontWeight(500),
                )
                if (alreadyOnTeam) {
                    SpanText(
                        text = "added",
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .color(Palette.textTertiary)
                            .backgroundColor(Palette.surfaceSubtle)
                            .borderRadius(Radius.sm)
                            .padding(topBottom = 2.px, leftRight = Spacing.xs),
                    )
                }
            }
            SpanText(
                text = "@${user.slackUserId}${user.email?.let { " · $it" } ?: ""}",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary),
            )
        }
    }
}
